import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, RotateCcw, Save } from '../../icons';
import {
  Button, EmptyState, PageHeader, Table,
  type TableColumn,
  StatTile,
  StatTiles
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listHireDates, saveHireDates,
  type HireDateEmployeeDto, type LeaveRuleDto,
} from '../../api/hr-admin';
import { asOfForYear, computeAnnualDays, describeTiers, parseDateOnly, tenureLabel, tenureMonths } from '../../lib/leave-rules';
import './AttendanceAdmin.css';

/**
 * 화면 C — **입사일 일괄 채우기** (근태 0단계, 2026-08-23).
 *
 * 설계: `docs/attendance-round2-sample.html` 화면 C · 「계획」 0 DATA.
 *
 * 왜 이 화면이 먼저인가: 연차는 입사일에서 시작한다. 지금 `hr_leave_quotas.annualTotal` 의
 * 기본값은 **근속과 무관한 15** 이고, 입사일 칸이 없어 근로기준법 제60조 계산을 시작조차 할 수
 * 없었다. 아홉 명이 채워지는 순간 잔여 연차가 "미설정" 에서 숫자로 바뀐다.
 *
 * 화면의 약속 둘:
 *   1) **칸에 넣으면 오른쪽 두 칸이 바로 바뀐다** — 근속과 그 해 연차를 즉시 다시 그린다.
 *      서버에 물어보지 않는다(한 글자마다 왕복하면 표가 덜컹거린다). 저장값은 서버가 계산한다.
 *   2) **저장 전에는 아무것도 바뀌지 않는다** — 고친 줄만 모아서 한 번에 보낸다.
 *
 * ── 정본 채택 (2026-09-06 · 채택 3단계 ③ 근태 · 기준 셋) ─────────────────────────
 * `PageHeader` · `Table`(rowHeader) · `Button` · `EmptyState`.
 *
 * ★★★ **입사일 칸만은 네이티브 `type="date"` 로 남겼다 — 정본 `DatePicker` 로 옮기면 회귀다.**
 *    정본 달력은 **달 단위 이동뿐**이라(이전 달 / 다음 달) 2026년에서 **2019년 4월 입사일**까지
 *    가려면 **88번** 눌러야 한다. 네이티브 칸은 그냥 «치면» 된다.
 *    「정본이 이긴다」는 규율에는 **기존 동작 회귀 금지**라는 단서가 붙어 있고, 이것이 그 자리다.
 *    → 정본 C1 에 **연도 이동이 생기기 전까지의 예외**로 적어 두었다.
 * ★★ 「연차 부여 규칙」 칸은 남겼다 — **회사마다 다른 값**이고, 표의 「연차」가 왜 그 숫자인지
 *    옆에서 대조하는 자리다(연차 관리와 같은 판단). 근기법 조문만 도움말로 보냈다.
 */

interface Draft { hireDate: string | null }

export function HireDatesPage(): JSX.Element {
  const [year] = useState(() => new Date().getFullYear());
  const [rule, setRule] = useState<LeaveRuleDto | null>(null);
  const [employees, setEmployees] = useState<HireDateEmployeeDto[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listHireDates(year);
      setRule(data.rule);
      setEmployees(data.employees);
      setDrafts({});
    } catch {
      setError('직원 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { void load(); }, [load]);

  const asOf = useMemo(() => asOfForYear(year), [year]);

  /** 화면에 보이는 값 = 고친 것이 있으면 그것, 없으면 서버 값. */
  const valueOf = useCallback(
    (e: HireDateEmployeeDto): string => drafts[e.userId]?.hireDate ?? e.hireDate ?? '',
    [drafts],
  );

  /** 근속·연차 미리보기 — 저장 전에도 이 자리에서 바로 답한다. */
  const preview = useCallback((e: HireDateEmployeeDto) => {
    const raw = valueOf(e);
    if (!raw || !rule) return { tenure: '미설정', days: null as number | null };
    const d = parseDateOnly(raw);
    if (!d) return { tenure: '미설정', days: null };
    return { tenure: tenureLabel(tenureMonths(d, asOf)), days: computeAnnualDays(d, asOf, rule) };
  }, [valueOf, rule, asOf]);

  const changed = useMemo(
    () => employees.filter((e) => {
      const draft = drafts[e.userId];
      if (!draft) return false;
      return (draft.hireDate ?? '') !== (e.hireDate ?? '');
    }),
    [employees, drafts],
  );

  const stats = useMemo(() => {
    const missing = employees.filter((e) => !valueOf(e)).length;
    const filled = employees.length - missing;
    const confirmed = employees.filter((e) => e.hireDate).length;
    return { missing, filled, confirmed, willConfirm: filled };
  }, [employees, valueOf]);

  const onSave = async () => {
    if (changed.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await saveHireDates(changed.map((e) => ({
        userId: e.userId,
        hireDate: drafts[e.userId]?.hireDate || null,
      })));
      setSavedAt(Date.now());
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(code === 'INVALID_DATE' ? '없는 날짜가 있습니다. 다시 확인해 주세요.'
        : code === 'FORBIDDEN' ? '이 화면은 회사 관리자만 쓸 수 있습니다.'
        : '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const columns: ReadonlyArray<TableColumn<HireDateEmployeeDto>> = useMemo(() => [
    { key: 'name', header: '이름', width: '120px', rowHeader: true, render: (e) => e.name },
    { key: 'team', header: '소속', width: '150px', render: (e) => e.teamName ?? '회사 직속' },
    { key: 'empNo', header: '사번', width: '120px', render: (e) => e.employeeNumber ?? '—' },
    {
      key: 'hireDate',
      header: '입사일',
      width: '170px',
      /*
        ★★★ 네이티브 날짜 칸이다 — 정본 `DatePicker` 는 **달 단위 이동뿐**이라 먼 과거
        입사일(2019년)까지 88번을 눌러야 한다. 여기서 갈아타면 «칠 수 있던 것을 못 치게» 된다.
      */
      render: (e) => (
        <input
          type="date"
          className="hra__date"
          aria-label={`${e.name} 입사일`}
          value={valueOf(e)}
          onChange={(ev) => setDrafts((d) => ({ ...d, [e.userId]: { hireDate: ev.target.value || null } }))}
        />
      ),
    },
    {
      key: 'tenure',
      header: '근속',
      width: '110px',
      render: (e) => {
        const p = preview(e);
        return <span className={p.days === null ? 'is-muted' : undefined}>{p.tenure}</span>;
      },
    },
    {
      key: 'annual',
      header: `${year} 연차`,
      width: '96px',
      align: 'right',
      render: (e) => {
        const p = preview(e);
        return <span className={p.days === null ? 'is-muted' : 'is-strong'}>{p.days === null ? '—' : `${p.days}일`}</span>;
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [valueOf, preview, year]);

  return (
    <div className="hra">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/hire-dates')}
        scope={scopeFor('/erp/attendance/hire-dates')}
        title="입사일 관리"
        description={`직원 ${employees.length}명 · 입사일이 없으면 연차가 시작되지 않습니다. 칸에 넣으면 오른쪽 두 칸이 바로 바뀝니다.`}
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p>연차 부여는 <b>근로기준법 제60조</b>를 따릅니다. 회사가 <b>더 줄 수는 있어도
                덜 줄 수는 없습니다</b> — 왼쪽 「연차 부여 규칙」이 지금 이 회사에 적용되는 판입니다.</p>
              <p><b>저장 전에는 아무것도 바뀌지 않습니다.</b> 고친 줄만 모아서 한 번에 보내고,
                변경은 감사에 남습니다.</p>
              <p>근속과 그 해 연차는 <b>칸에 넣는 순간</b> 화면이 다시 그립니다 — 서버에 물어보지
                않습니다. 실제로 저장되는 값은 서버가 다시 계산합니다.</p>
            </>
          ),
        }}
      />

      {/* ★ 이 칸은 산문이 아니라 «값»이다 — 회사마다 다르고, 표의 「연차」를 옆에서 대조한다. */}
      {rule && (
        <section className="hra__rule" aria-label="연차 부여 규칙">
          <h2 className="hra__rulehead">연차 부여 규칙</h2>
          <ul className="hra__rulelist">
            {describeTiers(rule).map((line) => <li key={line}>{line}</li>)}
          </ul>
        </section>
      )}

      {/* 정본 D9-2 요약 타일 (2026-09-06 신설) — 손짜기 `hra__stat` 에서 옮겨 왔다. */}
      <StatTiles ariaLabel="현황">
        {/* 이 화면이 있는 이유가 이 숫자다 — 비어 있으면 연차가 시작되지 않는다. */}
        <StatTile label="입사일 없음" value={stats.missing} unit="명" tone={stats.missing > 0 ? 'warning' : 'default'} />
        <StatTile label="이미 있음" value={stats.confirmed} unit="명" />
        <StatTile label="고친 줄" value={changed.length} unit="줄" />
        <StatTile label="저장하면 확정" value={stats.willConfirm} unit="명" />
      </StatTiles>

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {savedAt !== null && !error && (
        <p className="hra__ok" role="status"><Check size={14} aria-hidden />저장했습니다 — 그 해 연차가 다시 계산됐습니다.</p>
      )}

      <Table
        caption="직원 입사일"
        className="hra-tbl"
        columns={columns}
        rows={employees}
        rowKey={(e) => e.userId}
        loading={loading}
        // 고친 줄은 눈에 남는다 — 저장 전에 무엇을 건드렸는지가 보여야 한다.
        rowClassName={(e) => (changed.some((c) => c.userId === e.userId) ? 'is-changed' : undefined)}
        empty={<EmptyState title="직원이 없습니다." description="직원이 등록되면 여기에서 입사일을 채웁니다." />}
      />

      <footer className="hra__foot">
        <div className="hra__actions">
          <Button variant="secondary" iconLeft={<RotateCcw size={14} aria-hidden />} onClick={() => setDrafts({})} disabled={changed.length === 0 || saving}>
            되돌리기
          </Button>
          <Button iconLeft={<Save size={14} aria-hidden />} onClick={() => { void onSave(); }} disabled={changed.length === 0 || saving} loading={saving}>
            {changed.length}명 저장
          </Button>
        </div>
      </footer>
    </div>
  );
}
